import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AdminRole, getInitials } from '../../../index.server';
import { logoutCurrentSession } from '../../../services/SessionManager';
import { Tenant } from '../../../types/dataTypes';
import { useTenant } from '../../context/TenantContext';
import { User, useUser } from '../../context/UserContext';

import './Header.css';

type HeaderVisibilityContext = {
    currentUser: User | null;
    currentTenant: Tenant | null;
};

export interface HeaderMenuItem {
    to: string;
    label: string;
    iconClassName?: string;
    match?: 'exact' | 'prefix';
    isVisible?: (context: HeaderVisibilityContext) => boolean;
    isActive?: (pathname: string) => boolean;
    showWithoutTenant?: boolean;
}

interface HeaderProps {
    menuItems?: HeaderMenuItem[];
    tenantSwitchLabel?: string;
    noTenantSubtitle?: string;
    brandIcon?: React.ReactNode;
    portalLabel?: string;
    portalTone?: 'admin' | 'monitor' | 'neutral';
}

const navLinkBase = 'nav-link fw-semibold px-3 py-2 rounded-3 d-flex align-items-center gap-0';
const navLinkStyle = { fontSize: '13px' as const };
const navLinkActiveStyle = { ...navLinkStyle, background: '#eaf2fb' };

const HeaderNavLink = ({ item, active = false }: { item: HeaderMenuItem; active?: boolean }) => (
    <Link
        to={item.to}
        className={`${navLinkBase} ${active ? 'text-primary' : 'text-secondary'}`}
        style={active ? navLinkActiveStyle : navLinkStyle}
    >
        {item.iconClassName && <i className={`${item.iconClassName} me-1 mt-px`} />}
        {item.label}
    </Link>
);

const UserBadge = ({ currentUser }: { currentUser: User | null }) => (
    <>
        <div
            className="rounded-circle bg-light border d-flex align-items-center justify-content-center fw-bold text-sm color-text-secondary" style={{ width: '30px', height: '30px', flexShrink: 0 }}
        >
            {!!currentUser ? getInitials(currentUser.name) : ''}
        </div>
        <span className="fw-semibold text-secondary">{currentUser?.name}</span>
    </>
);

const DefaultBrandIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24">
        <path d="M9 3h6M9 3v7l-4 8a1 1 0 0 0 .9 1.5h12.2A1 1 0 0 0 19 18l-4-8V3" />
        <path d="M8.5 15.5h7" />
    </svg>
);

const LogoutButton = ({ onClick }: { onClick: () => Promise<void> }) => {
    return (
        <button type="button" className="header-session-action" onClick={() => void onClick()}>
            <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
            <span>Abmelden</span>
        </button>
    );
};

const NoTenantHeader = ({
    currentUser,
    subtitle,
    brandIcon,
    portalLabel,
    portalTone,
    onLogout,
    menuItems = [],
}: {
    currentUser: User | null;
    subtitle: string;
    brandIcon?: React.ReactNode;
    portalLabel: string;
    portalTone: 'admin' | 'monitor' | 'neutral';
    onLogout: () => Promise<void>;
    menuItems?: HeaderMenuItem[];
}) => {
    return (
        <div className="container-fluid px-4 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
                {/* <div className="portal-mode-slot">
                    <span className={`portal-mode-badge ${portalTone}`}>{portalLabel}</span>
                </div> */}
                <Link className="navbar-brand d-flex align-items-center gap-2 text-decoration-none" to="/tenant-picker">
                <div className="navbar-brand-icon">
                    <div className="navbar-brand-icon-content">{brandIcon || <DefaultBrandIcon />}</div>
                </div>
                <div>
                    <div className="fw-bold text-body-lg color-text-primary" style={{ lineHeight: 1.2 }}>Saludari</div>
                    <div className="brand-sub">{subtitle}</div>
                </div>
            </Link>
                {menuItems.length > 0 && <div className="vr mx-1 opacity-25"></div>}
                {menuItems.map(item => (
                    <HeaderNavLink key={`${item.to}-${item.label}`} item={item} />
                ))}
            </div>
            <div className="d-flex align-items-center gap-2">
                <UserBadge currentUser={currentUser} />
                {currentUser ? (
                    <LogoutButton onClick={onLogout} />
                ) : null}
            </div>
        </div>
    );
};

export const Header = ({
    menuItems = [],
    tenantSwitchLabel = 'Mieter wechseln',
    noTenantSubtitle = 'Admin portal',
    brandIcon,
    portalLabel = 'ADMIN',
    portalTone = 'admin',
}: HeaderProps) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { currentUser, setCurrentUser } = useUser();
    const { currentTenant, setCurrentTenant } = useTenant();

    const handleLogout = React.useCallback(async () => {
        const didLogout = await logoutCurrentSession();

        if (!didLogout) {
            return;
        }

        setCurrentTenant(null);
        setCurrentUser(null);
        navigate('/login');
    }, [navigate, setCurrentTenant, setCurrentUser]);

    if (!currentTenant) {
        return (
            <nav className="navbar app-navbar navbar-light position-sticky top-0 px-3" style={{ zIndex: 100 }}>
                <NoTenantHeader
                    currentUser={currentUser}
                    subtitle={noTenantSubtitle}
                    brandIcon={brandIcon}
                    portalLabel={portalLabel}
                    portalTone={portalTone}
                    onLogout={handleLogout}
                    menuItems={menuItems.filter(item =>
                        item.showWithoutTenant &&
                        (!item.isVisible || item.isVisible({ currentUser, currentTenant }))
                    )}
                />
            </nav>
        );
    }

    const onTenantSwitcherClick = () => {
        setCurrentTenant(null);
        navigate('/tenant-picker');
    };

    const visibleMenuItems = menuItems.filter(item => {
        if (!item.isVisible) {
            return true;
        }
        return item.isVisible({ currentUser, currentTenant });
    });

    return (
        <nav className="navbar app-navbar navbar-light position-sticky top-0 px-3" style={{ zIndex: 100 }}>
            <div className="d-flex align-items-center gap-2 h-100">
                {/* <div className="portal-mode-slot">
                    <span className={`portal-mode-badge ${portalTone}`}>{portalLabel}</span>
                </div> */}
                <Link to="#" className="study-switcher" onClick={onTenantSwitcherClick}>
                    <div className="study-switcher-icon">
                        <i className="bi bi-grid-3x3-gap-fill text-white"></i>
                    </div>
                    {currentTenant.tenantName}
                    <span className="text-secondary fw-normal">{tenantSwitchLabel}</span>
                </Link>

                <div className="vr mx-1 opacity-25"></div>

                {visibleMenuItems.map(item => {
                    const active = item.isActive
                        ? item.isActive(pathname)
                        : item.match === 'exact'
                            ? pathname === item.to
                            : pathname === item.to || pathname.startsWith(`${item.to}/`);

                    return (
                        <HeaderNavLink key={`${item.to}-${item.label}`} item={item} active={active} />
                    );
                })}
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto">
                <LogoutButton onClick={handleLogout} />
                <UserBadge currentUser={currentUser} />
            </div>
        </nav>
    );
};

export const defaultAdminHeaderMenuItems: HeaderMenuItem[] = [
    { to: '/users', label: 'Teilnehmer', iconClassName: 'bi bi-people', match: 'prefix' },
    { to: '/manageUserProperties', label: 'Teilnehmereigenschaften', iconClassName: 'bi bi-tags', match: 'prefix' },
    {
        to: '/advanced-users',
        label: 'Admins',
        iconClassName: 'bi bi-shield-check',
        match: 'prefix',
        isVisible: ({ currentTenant }) => currentTenant?.role === AdminRole.TENANT_ADMIN,
    },
];
